'use client';

import { confirmSignIn, signIn, SignInOutput, getCurrentUser, fetchAuthSession, signUp } from 'aws-amplify/auth';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { fetchUserSettingsByOwner, fetchFirstShopifyAppUrl, AccountService } from '@/api/account';
import { getIsLoggedIn } from '@/api/amplify/helpers';
import { GOOGLE_AUTH_QUERY_PARAMS, SHOPIFY_SIGNUP_QUERY_PARAMS } from '@/constants/queryParams';
import { useUserDataStore } from '@/stores/userDataStore';
import { clearGoogleAuthUrlParams, clearShopifySignUpUrlParams } from '@/utils/queryParams';
import { AuthActions as UseAuthActionsType, useAuthActions } from './useAuthActions';
import { useRouter } from 'next/navigation';

interface AuthState {
  isLoading: boolean;
  error: string | null;
  isLoggedIn: boolean | null;
  isSignUp: boolean;
  email: string;
  password: string;
}

interface GoogleLoginParams {
  integration?: string | null;
  shopifyShop?: string | null;
  utmSource?: string | null;
  redirectUrl?: string | null;
}

interface AuthActions extends UseAuthActionsType {
  signIn: () => void;
  signUp: () => void;
  signOut: () => void;
  googleLogin: () => void;
  toggleSignUp: () => void;
  setEmail: (email: string) => void;
  setPassword: (password: string) => void;
}

const googleLogin = async (params: Partial<GoogleLoginParams>) => {
  const queryParams = new URLSearchParams(params as Record<string, string>);
  const url = `${process.env.NEXT_PUBLIC_API_URL}/auth/google/login?${queryParams}`;

  window.open(url, '_self', 'noopener, noreferrer');
};

/*
  Suggestion:
    Feels like this hook doesn't need to manage email, password, errors
    But only answer a question if a quest loged in and provide auth actions

    TODO:
      - remove email, password, error, isLoading, isSignUp from state
        - it's here just to manage data in login/signup form - better to manage it in those components (using react-hook-form + zod?)
      - re-write actions to be pure (only work with params)
      - move all actions to a separate hook: useAuthActions (to have ability to use only actions and avoid re-renders)
      - use this hook here
*/
export const useAuth = (): [AuthState, AuthActions] => {
  const actions = useAuthActions();
  const currentAppUrl = useUserDataStore.getState().appUrl;
  const router = useRouter();

  const [state, setState] = useState<AuthState>({
    isLoading: true,
    error: null,
    isLoggedIn: null,
    isSignUp: false,
    email: '',
    password: '',
  });

  const searchParams = useSearchParams();
  const queryTempId = searchParams?.get(SHOPIFY_SIGNUP_QUERY_PARAMS.TEMPORARY_ID);
  const shop = searchParams?.get(SHOPIFY_SIGNUP_QUERY_PARAMS.SHOP);
  const userId = searchParams?.get(GOOGLE_AUTH_QUERY_PARAMS.USER_ID);
  const auth = searchParams?.get(GOOGLE_AUTH_QUERY_PARAMS.AUTH);
  const integration = searchParams.get(SHOPIFY_SIGNUP_QUERY_PARAMS.INTEGRATION) || undefined;
  const shopifyShop = searchParams.get('shopifyShop') || undefined;
  const utmSource = searchParams.get('utm_source') || undefined;
  const TWO_MINUTES = 2 * 60 * 1000;

  const monthPastUnixTime = (startTime) => {
    return (Date.now() / 1000 - startTime) / 86400 > 28;
  };

  const sendCustomChallengeAnswerIfNeeded = async (signInResult: SignInOutput, auth: string) => {
    if (
      signInResult.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE' &&
      signInResult.nextStep.additionalInfo?.distraction === 'Yes'
    ) {
      try {
        await confirmSignIn({
          challengeResponse: auth,
        });
      } catch (error) {
        console.error('Error confirming sign in', error);
        throw error;
      }
    }
  };

  const handleLoginFromRedirect = async () => {
    if (!userId || !auth) {
      return;
    }

    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      error: null,
    }));

    try {
      const signInResult = await signIn({
        username: userId,
        password: auth,
      });
      await sendCustomChallengeAnswerIfNeeded(signInResult, auth);

      await getUserDataOnSignIn();

      clearGoogleAuthUrlParams();
    } catch (error) {
      handleAuthError(error);
    }
  };

  const checkAuthStateOnLoad = async () => {
    try {
      if (userId && auth) {
        await handleLoginFromRedirect();
        return;
      }
      const isLoggedIn = await getIsLoggedIn();

      if (isLoggedIn && shop) {
        useUserDataStore.getState().setAppUrl(shop);
      }

      if (isLoggedIn && queryTempId) {
        const appKey = useUserDataStore.getState().appKey;
        await handleFinishShopifyAuth({ appKey });
      }

      setIsLoggedInState({ isLoggedIn });
    } catch (error) {
      console.error('Error during checking if user is logged in:', error);
    }
  };

  const setIsLoggedInState = ({ isLoggedIn }) => {
    setState((prevState) => ({
      ...prevState,
      isLoggedIn,
      isLoading: false,
    }));
  };

  const handleAuthError = (error: any) => {
    setState((prevState) => ({
      ...prevState,
      error: error.message,
      isLoading: false,
    }));
    console.error(error);
  };

  const getUserDataOnSignIn = async () => {
    const userData = await getCurrentUser();
    if (!userData) {
      return;
    }
    const owner = userData.username;
    const userSettings = await fetchUserSettingsByOwner({
      owner,
    });

    const appKey = userSettings.items[0]?.appKey;
    const mainShopifyAppUrl = await fetchFirstShopifyAppUrl(appKey);

    const appUrl = shop || mainShopifyAppUrl;

    if (queryTempId) {
      await handleFinishShopifyAuth({ appKey });
    }
    if (appKey) {
      useUserDataStore.getState().updateUserData({ appKey, appUrl, owner });
    }
    setIsLoggedInState({ isLoggedIn: true });
  };

  const signInWithEmailAndPassword = useCallback(async () => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      error: null,
    }));

    try {
      await signIn({
        username: state.email,
        password: state.password,
      });
      await getUserDataOnSignIn();
    } catch (error) {
      handleAuthError(error);
    }
  }, [state.email, state.password]);

  const signUpWithEmailAndPassword = useCallback(async () => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      error: null,
    }));

    try {
      await signUp({
        username: state.email,
        password: state.password,
        options: {
          userAttributes: {
            email: state.email,
          },
        },
      });
      await signInWithEmailAndPassword();

      clearShopifySignUpUrlParams();
    } catch (error) {
      handleAuthError(error);
    }
  }, [state.email, state.password]);

  const signOut = useCallback(async () => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      error: null,
    }));

    try {
      await signOut();
      useUserDataStore.getState().removeUserData();
      setIsLoggedInState({ isLoggedIn: false });
    } catch (error) {
      handleAuthError(error);
    }
  }, []);

  const refreshSession = useCallback(
    async (attempt) => {
      try {
        const auth = await fetchAuthSession();
        const authenticatedAt = auth.tokens?.accessToken.payload.auth_time;
        if (monthPastUnixTime(authenticatedAt)) {
          console.log('session timeout');
          signOut();
        }
      } catch (err) {
        console.log(err);

        attempt = attempt || 0;
        if (attempt > 3 || err === 'No current user') {
          signOut();
          return;
        }

        setTimeout(() => refreshSession(attempt + 1), 200);
      }
    },
    [signOut]
  );

  const logInWithGoogle = useCallback(async () => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      error: null,
    }));
    try {
      await googleLogin({
        integration,
        shopifyShop,
        utmSource,
        redirectUrl: window.location.href,
      });
    } catch (error) {
      handleAuthError(error);
    }
  }, []);

  const toggleSignUp = () => {
    setState((prevState) => ({
      ...prevState,
      isSignUp: !prevState.isSignUp,
    }));
  };

  const setEmail = (email: string) => {
    setState((prevState) => ({
      ...prevState,
      email,
    }));
  };

  const setPassword = (password: string) => {
    setState((prevState) => ({
      ...prevState,
      password,
    }));
  };

  const handleFinishShopifyAuth = async ({ appKey }) => {
    router.replace('/');

    return await AccountService.completeShopifyAuth({
      tempId: queryTempId,
      appKey: appKey,
    });
  };

  useEffect(() => {
    if (!state.isLoggedIn) {
      return;
    }

    refreshSession(0);
    const interval = setInterval(() => {
      refreshSession(0);
    }, TWO_MINUTES);

    return () => {
      clearInterval(interval);
    };
  }, [refreshSession, state.isLoggedIn]);

  useEffect(() => {
    checkAuthStateOnLoad();
  }, [state.isSignUp]);

  useEffect(() => {
    const isSignUp = shop && queryTempId;

    if (isSignUp) {
      setState((prevState) => ({
        ...prevState,
        isSignUp: true,
      }));
    }

    if (currentAppUrl && shop && currentAppUrl !== shop) {
      signOut();
    }
  }, [shop, queryTempId, currentAppUrl]);

  return [
    state,
    {
      signIn: signInWithEmailAndPassword,
      signUp: signUpWithEmailAndPassword,
      signOut,
      googleLogin: logInWithGoogle,
      toggleSignUp,
      setEmail,
      setPassword,
      ...actions,
    },
  ];
};

export default useAuth;
